import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ShoppingCart, Scan, Plus, Minus, Trash2, CreditCard, Banknote, Gift,
  Truck, Store, Printer, Search, X, Check, Loader2, AlertCircle,
  Receipt, DollarSign, User, Phone, MapPin, Clock, Package,
  Bluetooth, Usb, Volume2, VolumeX, ChevronDown, ChevronUp,
  Calculator, Percent, Tag, ArrowRight, RefreshCw, Home,
  QrCode, Download, Users, Camera
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Payment methods
const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote, color: 'green' },
  { id: 'card', name: 'Credit/Debit Card', icon: CreditCard, color: 'blue' },
  { id: 'etransfer', name: 'Interac e-Transfer', icon: Phone, color: 'orange' },
  { id: 'giftcard', name: 'Gift Card', icon: Gift, color: 'purple' },
];

const POSCashier = ({ user, onBack }) => {
  // Cart state
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Customer state
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: 'Ottawa',
    postalCode: ''
  });
  const [isDelivery, setIsDelivery] = useState(false);
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardBalance, setGiftCardBalance] = useState(null);
  const [discount, setDiscount] = useState({ type: 'none', value: 0 });
  const [processing, setProcessing] = useState(false);
  
  // Scanner state
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const barcodeBuffer = useRef('');
  const barcodeTimeout = useRef(null);
  
  // Receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  
  // Product catalog cache
  const [allProducts, setAllProducts] = useState([]);

  // Transfer state
  const [showTransferInput, setShowTransferInput] = useState(false);
  const [transferCode, setTransferCode] = useState('');
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [activeTransfer, setActiveTransfer] = useState(null);
  const [pendingTransfers, setPendingTransfers] = useState([]);

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (res.ok) {
          const data = await res.json();
          setAllProducts(data);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Fetch pending transfers periodically
  useEffect(() => {
    const fetchPendingTransfers = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/pos/transfers/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPendingTransfers(data.transfers || []);
        }
      } catch (error) {
        console.error('Error fetching pending transfers:', error);
      }
    };
    
    fetchPendingTransfers();
    const interval = setInterval(fetchPendingTransfers, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Lookup transfer by code
  const lookupTransfer = async (code) => {
    if (!code || code.length !== 6) {
      toast.error('Please enter a 6-digit transfer code');
      return;
    }
    
    setLoadingTransfer(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/pos/transfer/${code}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const transfer = await res.json();
        
        // Claim the transfer
        const claimRes = await fetch(`${API_URL}/api/pos/transfer/${code}/claim`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (claimRes.ok) {
          const claimData = await claimRes.json();
          setActiveTransfer(claimData.transfer);
          
          // Load transfer items into cart
          const transferItems = transfer.items.map(item => ({
            id: item.product_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            taxable: item.taxable || false,
            image: item.image || null,
            isTransferItem: true
          }));
          
          setCart(transferItems);
          setCustomerInfo({
            ...customerInfo,
            name: transfer.customer_name || '',
            email: transfer.customer_email || '',
            phone: transfer.customer_phone || ''
          });
          
          setShowTransferInput(false);
          setTransferCode('');
          toast.success(`Transfer loaded! Customer: ${transfer.customer_name}`);
          
          // Play success sound
          if (soundEnabled) {
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              oscillator.frequency.value = 800;
              oscillator.type = 'sine';
              gainNode.gain.value = 0.2;
              oscillator.start();
              setTimeout(() => {
                oscillator.frequency.value = 1200;
                setTimeout(() => oscillator.stop(), 100);
              }, 100);
            } catch (e) {
              // Audio not supported - ignore
            }
          }
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Transfer not found');
      }
    } catch (error) {
      console.error('Transfer lookup error:', error);
      toast.error('Failed to lookup transfer');
    } finally {
      setLoadingTransfer(false);
    }
  };

  // Complete a transfer
  const completeTransfer = async () => {
    if (!activeTransfer) return;
    
    if (paymentMethod === 'cash' && parseFloat(cashReceived) < total) {
      toast.error('Insufficient cash received');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({
        payment_method: paymentMethod
      });
      
      if (paymentMethod === 'cash' && cashReceived) {
        params.append('cash_received', parseFloat(cashReceived));
      }
      
      const res = await fetch(`${API_URL}/api/pos/transfer/${activeTransfer.transfer_code}/complete?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        setLastOrder({
          order_number: result.order_number,
          items: cart,
          subtotal,
          tax,
          discount: discountAmount,
          delivery_fee: deliveryFee,
          total: result.total,
          payment_method: result.payment_method,
          cash_received: result.cash_received,
          change_due: result.change_due,
          customer_name: result.customer_name,
          isTransfer: true
        });
        setShowReceipt(true);
        toast.success(`Order #${result.order_number} completed!`);
        
        // Clear everything
        clearCart();
        setActiveTransfer(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to complete transfer');
      }
    } catch (error) {
      console.error('Complete transfer error:', error);
      toast.error('Error completing transfer');
    } finally {
      setProcessing(false);
    }
  };

  // Cancel active transfer
  const cancelTransfer = () => {
    setActiveTransfer(null);
    clearCart();
    toast.info('Transfer cancelled');
  };

  // Handle barcode scan (used by both keyboard scanner and camera)
  const handleBarcodeScanned = useCallback((barcode) => {
    // Clean the barcode - remove any QR prefix if present
    let cleanBarcode = barcode;
    if (barcode.startsWith('UPLANDS-PRODUCT:')) {
      cleanBarcode = barcode.replace('UPLANDS-PRODUCT:', '');
    }
    
    setLastScannedBarcode(cleanBarcode);
    
    // Play beep sound
    if (soundEnabled) {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 1000;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 100);
      } catch (e) {
        // Audio not supported
      }
    }

    // Find product by barcode or name
    const product = allProducts.find(p => 
      p.barcode === cleanBarcode || 
      p.id === cleanBarcode ||
      p.name?.toLowerCase() === cleanBarcode.toLowerCase()
    );
    
    if (product) {
      // Add to cart inline
      setCart(prev => {
        const existing = prev.find(item => item.id === product.id);
        if (existing) {
          return prev.map(item =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        }
        return [...prev, { ...product, quantity: 1 }];
      });
      toast.success(`Added: ${product.name}`);
    } else {
      toast.error(`Product not found: ${cleanBarcode}`);
    }
  }, [allProducts, soundEnabled]);

  // Barcode scanner listener (keyboard/USB scanner)
  useEffect(() => {
    if (!scannerEnabled) return;

    const handleKeyPress = (e) => {
      // Ignore if typing in an input field (except for barcode scanning)
      if (e.target.tagName === 'INPUT' && e.target.type !== 'text') return;
      if (e.target.closest('.no-scanner')) return;

      // Clear timeout on each keypress
      if (barcodeTimeout.current) {
        clearTimeout(barcodeTimeout.current);
      }

      // Add character to buffer
      if (e.key === 'Enter') {
        // Process barcode
        if (barcodeBuffer.current.length >= 4) {
          handleBarcodeScanned(barcodeBuffer.current);
        }
        barcodeBuffer.current = '';
      } else if (e.key.length === 1) {
        barcodeBuffer.current += e.key;
      }

      // Clear buffer after 100ms of no input (barcode scanners are fast)
      barcodeTimeout.current = setTimeout(() => {
        barcodeBuffer.current = '';
      }, 100);
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [scannerEnabled, allProducts, handleBarcodeScanned]);

  // Add product to cart
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  // Update quantity
  const updateQuantity = (productId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setDiscount({ type: 'none', value: 0 });
    setCustomerInfo({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: 'Ottawa',
      postalCode: ''
    });
    setIsDelivery(false);
    setCashReceived('');
    setGiftCardCode('');
    setGiftCardBalance(null);
  };

  // Search products
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    const filtered = allProducts.filter(p =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.barcode?.includes(query) ||
      p.category?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10);
    
    setSearchResults(filtered);
    setSearching(false);
  };

  // Check gift card balance
  const checkGiftCardBalance = async () => {
    if (!giftCardCode) return;
    
    try {
      const res = await fetch(`${API_URL}/api/gift-cards/balance/${giftCardCode}`);
      if (res.ok) {
        const data = await res.json();
        setGiftCardBalance(data.balance);
        toast.success(`Gift card balance: $${data.balance.toFixed(2)}`);
      } else {
        toast.error('Invalid gift card code');
        setGiftCardBalance(null);
      }
    } catch (error) {
      toast.error('Error checking gift card');
    }
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const taxableAmount = cart
    .filter(item => item.taxable)
    .reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = taxableAmount * 0.13; // 13% HST
  
  let discountAmount = 0;
  if (discount.type === 'percent') {
    discountAmount = subtotal * (discount.value / 100);
  } else if (discount.type === 'fixed') {
    discountAmount = discount.value;
  }
  
  const deliveryFee = isDelivery ? 5.99 : 0;
  const total = subtotal + tax - discountAmount + deliveryFee;
  const change = paymentMethod === 'cash' && cashReceived ? parseFloat(cashReceived) - total : 0;

  // Process transaction
  const processTransaction = async () => {
    // If there's an active transfer, complete it instead
    if (activeTransfer) {
      await completeTransfer();
      return;
    }
    
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (isDelivery && (!customerInfo.name || !customerInfo.phone || !customerInfo.address)) {
      toast.error('Please fill in delivery information');
      return;
    }

    if (paymentMethod === 'cash' && parseFloat(cashReceived) < total) {
      toast.error('Insufficient cash received');
      return;
    }

    if (paymentMethod === 'giftcard' && (!giftCardBalance || giftCardBalance < total)) {
      toast.error('Insufficient gift card balance');
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Use dedicated POS transaction endpoint
      const posData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          taxable: item.taxable || false,
          image: item.image || null
        })),
        subtotal,
        tax,
        discount: discountAmount,
        delivery_fee: deliveryFee,
        total,
        payment_method: paymentMethod,
        order_type: isDelivery ? 'delivery' : 'in_store',
        customer_info: isDelivery ? customerInfo : { name: customerInfo.name || 'Walk-in Customer' },
        gift_card_code: paymentMethod === 'giftcard' ? giftCardCode : null,
        cash_received: paymentMethod === 'cash' ? parseFloat(cashReceived) : null,
        change_due: paymentMethod === 'cash' ? Math.max(0, change) : null,
        processed_by: user?.name || 'POS'
      };

      const res = await fetch(`${API_URL}/api/pos/transaction`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(posData)
      });

      if (res.ok) {
        const order = await res.json();
        setLastOrder({ 
          ...posData, 
          order_number: order.order_number, 
          id: order.id,
          items: posData.items,
          processed_by: order.processed_by
        });
        setShowReceipt(true);
        toast.success(`Order #${order.order_number} completed!`);
        
        // Play success sound
        if (soundEnabled) {
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 800;
            oscillator.type = 'sine';
            gainNode.gain.value = 0.2;
            oscillator.start();
            setTimeout(() => {
              oscillator.frequency.value = 1000;
              setTimeout(() => oscillator.stop(), 100);
            }, 100);
          } catch (e) {
            // Audio not supported - ignore
          }
        }
        
        // Clear cart for next customer
        clearCart();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process transaction');
      }
    } catch (error) {
      console.error('Transaction error:', error);
      toast.error('Error processing transaction');
    } finally {
      setProcessing(false);
    }
  };

  // Print receipt
  const printReceipt = () => {
    if (!lastOrder) return;
    
    const receiptWindow = window.open('', '_blank', 'width=400,height=600');
    receiptWindow.document.write(`
      <html>
        <head>
          <title>Receipt - Order #${lastOrder.order_number}</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; padding: 20px; max-width: 300px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .header h1 { margin: 0; font-size: 16px; }
            .header p { margin: 5px 0; }
            .items { border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; margin: 5px 0; }
            .totals { margin-bottom: 10px; }
            .total-line { display: flex; justify-content: space-between; }
            .grand-total { font-weight: bold; font-size: 14px; border-top: 1px solid #000; padding-top: 5px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>UPLANDS FOOD MARKET</h1>
            <p>Available 24/7, 365 Days - All Brands</p>
            <p>613-702-9143</p>
            <p>${new Date().toLocaleString()}</p>
            <p>Order #${lastOrder.order_number}</p>
          </div>
          <div class="items">
            ${lastOrder.items.map(item => `
              <div class="item">
                <span>${item.quantity}x ${item.name}</span>
                <span>$${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>
          <div class="totals">
            <div class="total-line"><span>Subtotal:</span><span>$${lastOrder.subtotal.toFixed(2)}</span></div>
            <div class="total-line"><span>Tax (HST 13%):</span><span>$${lastOrder.tax.toFixed(2)}</span></div>
            ${lastOrder.discount > 0 ? `<div class="total-line"><span>Discount:</span><span>-$${lastOrder.discount.toFixed(2)}</span></div>` : ''}
            ${lastOrder.delivery_fee > 0 ? `<div class="total-line"><span>Delivery:</span><span>$${lastOrder.delivery_fee.toFixed(2)}</span></div>` : ''}
            <div class="total-line grand-total"><span>TOTAL:</span><span>$${lastOrder.total.toFixed(2)}</span></div>
            ${lastOrder.payment_method === 'cash' ? `
              <div class="total-line"><span>Cash:</span><span>$${lastOrder.cash_received.toFixed(2)}</span></div>
              <div class="total-line"><span>Change:</span><span>$${lastOrder.change_due.toFixed(2)}</span></div>
            ` : ''}
          </div>
          <div class="footer">
            <p>Payment: ${lastOrder.payment_method.toUpperCase()}</p>
            <p>Thank you for shopping with us!</p>
            <p>www.asap247allbrands.com</p>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  // Quick amount buttons for cash
  const quickAmounts = [5, 10, 20, 50, 100];

  // Mobile view state
  const [mobileView, setMobileView] = useState('products'); // 'products' | 'cart'

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-2 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBack}
              className="text-white hover:bg-white/20 p-1 sm:p-2"
            >
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline ml-2">Exit POS</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline">POS / CASHIER</span>
              <span className="sm:hidden">POS</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Cashier Name - hidden on very small screens */}
            <div className="hidden md:flex bg-white/10 rounded-lg px-2 py-1">
              <span className="text-xs sm:text-sm">Cashier: <strong>{user?.name || 'Admin'}</strong></span>
            </div>
          </div>
        </div>
        
        {/* Scanner Controls Row */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/20">
          {/* Scanner Type Selector */}
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <span className="text-xs text-white/80 hidden sm:inline">Scanner:</span>
            
            {/* Camera Scanner - PROMINENT */}
            <Button 
              onClick={() => setShowCameraScanner(true)}
              className="bg-white text-green-700 hover:bg-green-50 gap-1 h-8 px-3 font-semibold"
              data-testid="pos-camera-scan-btn"
            >
              <Camera className="w-4 h-4" />
              <span className="text-sm">📱 Camera</span>
            </Button>
            
            {/* USB Scanner Toggle */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${scannerEnabled ? 'bg-green-500/30' : 'bg-white/10'}`}>
              <Usb className="w-3 h-3" />
              <span className="text-xs hidden sm:inline">USB</span>
              <Switch 
                checked={scannerEnabled} 
                onCheckedChange={setScannerEnabled}
                className="scale-75"
              />
            </div>
            
            {/* Sound Toggle */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/20 h-8 w-8 p-0"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Last Scanned Barcode */}
          {lastScannedBarcode && (
            <Badge variant="outline" className="bg-green-500/30 text-white border-white/30 text-xs">
              Last: {lastScannedBarcode}
            </Badge>
          )}
        </div>
      </div>

      {/* Mobile View Toggle */}
      <div className="md:hidden flex bg-white border-b">
        <button
          onClick={() => setMobileView('products')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
            mobileView === 'products' ? 'bg-green-50 text-green-700 border-b-2 border-green-600' : 'text-gray-600'
          }`}
        >
          <Search className="w-4 h-4" />
          Products
        </button>
        <button
          onClick={() => setMobileView('cart')}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 relative ${
            mobileView === 'cart' ? 'bg-green-50 text-green-700 border-b-2 border-green-600' : 'text-gray-600'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          Cart
          {cart.length > 0 && (
            <span className="absolute top-2 right-1/4 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Product Search & Results */}
        <div className={`${mobileView === 'products' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 flex-col border-r bg-white`}>
          {/* Search Bar */}
          <div className="p-3 sm:p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 text-base sm:text-lg h-10 sm:h-12 no-scanner"
              />
            </div>
            {/* Quick scan button in search area on mobile */}
            <Button 
              onClick={() => setShowCameraScanner(true)}
              className="w-full mt-2 md:hidden bg-green-600 hover:bg-green-700 text-white gap-2"
            >
              <Camera className="w-5 h-5" />
              Open Camera to Scan Product
            </Button>
          </div>

          {/* Search Results or Product Grid */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            {searchQuery.length >= 2 ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 mb-3">
                  {searching ? 'Searching...' : `${searchResults.length} results for "${searchQuery}"`}
                </p>
                {searchResults.map(product => (
                  <div 
                    key={product.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      addToCart(product);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-6 h-6 m-3 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.barcode || 'No barcode'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">${product.price?.toFixed(2)}</p>
                      <Badge variant="outline" className="text-xs">{product.category}</Badge>
                    </div>
                    <Button size="sm" className="bg-green-500 hover:bg-green-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Camera className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">Ready to Scan</h3>
                <p className="text-sm text-gray-400 mb-4">Scan a barcode or search for products</p>
                
                {/* Camera Scan Button - Prominent */}
                <Button 
                  onClick={() => setShowCameraScanner(true)}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2 mb-4"
                  size="lg"
                >
                  <Camera className="w-5 h-5" />
                  Use Phone Camera to Scan
                </Button>
                
                <div className="mt-4 flex items-center justify-center gap-4 flex-wrap">
                  <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${scannerEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Usb className="w-4 h-4" />
                    USB {scannerEnabled ? '✓' : 'Off'}
                  </div>
                  <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-gray-100 text-gray-500">
                    <Bluetooth className="w-4 h-4" />
                    Bluetooth
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-3 sm:p-4 border-t bg-gray-50">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  // Add gift card product
                  const giftCardProduct = {
                    id: `giftcard_${Date.now()}`,
                    name: 'Gift Card',
                    price: 25,
                    quantity: 1,
                    isGiftCard: true,
                    taxable: false
                  };
                  setCart(prev => [...prev, giftCardProduct]);
                  toast.success('Gift card added - adjust amount as needed');
                }}
              >
                <Gift className="w-4 h-4 mr-2" />
                Add Gift Card
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setDiscount({ type: 'percent', value: 10 })}
              >
                <Percent className="w-4 h-4 mr-2" />
                10% Discount
              </Button>
            </div>
            
            {/* Receive Transfer Section */}
            <div className="mt-3 pt-3 border-t">
              {!showTransferInput ? (
                <Button 
                  variant="outline" 
                  className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                  onClick={() => setShowTransferInput(true)}
                  data-testid="receive-transfer-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Receive Customer Transfer
                  {pendingTransfers.length > 0 && (
                    <Badge className="ml-2 bg-blue-500">{pendingTransfers.length}</Badge>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-blue-600">Enter Transfer Code</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setShowTransferInput(false);
                        setTransferCode('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={transferCode}
                      onChange={(e) => setTransferCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-xl font-mono tracking-widest no-scanner"
                      maxLength={6}
                      data-testid="transfer-code-input"
                    />
                    <Button 
                      onClick={() => lookupTransfer(transferCode)}
                      disabled={transferCode.length !== 6 || loadingTransfer}
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid="lookup-transfer-btn"
                    >
                      {loadingTransfer ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Pending Transfers List */}
                  {pendingTransfers.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">Recent transfers waiting:</p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {pendingTransfers.slice(0, 3).map(transfer => (
                          <div 
                            key={transfer.id}
                            className="flex items-center justify-between p-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100"
                            onClick={() => {
                              setTransferCode(transfer.transfer_code);
                              lookupTransfer(transfer.transfer_code);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-500" />
                              <span className="text-sm font-medium">{transfer.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-green-600">${transfer.total.toFixed(2)}</span>
                              <Badge variant="outline" className="text-xs font-mono">{transfer.transfer_code}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Cart & Payment */}
        <div className={`${mobileView === 'cart' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 flex-col bg-gray-50`}>
          {/* Active Transfer Banner */}
          {activeTransfer && (
            <div className="bg-blue-500 text-white px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                <div>
                  <p className="font-medium text-sm sm:text-base">Transfer: {activeTransfer.customer_name}</p>
                  <p className="text-xs sm:text-sm text-blue-100">Code: {activeTransfer.transfer_code}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={cancelTransfer}
                className="text-white hover:bg-blue-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                Cart ({cart.length})
                {activeTransfer && (
                  <Badge className="bg-blue-500 ml-2 text-xs">Transfer</Badge>
                )}
              </h2>
              {cart.length > 0 && !activeTransfer && (
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 hover:text-red-600 h-8 px-2">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-gray-400">
                <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm sm:text-base">Cart is empty</p>
                <p className="text-xs mt-2">Scan products or search to add items</p>
                <Button 
                  onClick={() => {
                    setMobileView('products');
                    setShowCameraScanner(true);
                  }}
                  className="mt-4 md:hidden bg-green-600 hover:bg-green-700"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Scan Product
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map(item => (
                  <Card key={item.id} className={`p-2 sm:p-3 ${item.isTransferItem ? 'border-blue-200 bg-blue-50/50' : ''}`}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : item.isGiftCard ? (
                          <Gift className="w-5 h-5 m-2.5 text-purple-500" />
                        ) : (
                          <Package className="w-5 h-5 m-2.5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{item.name}</p>
                        <p className="text-sm text-gray-500">${item.price?.toFixed(2)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-8 h-8 p-0"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-8 h-8 p-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="font-bold text-green-600 w-20 text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-600 w-8 h-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Delivery Option */}
          {cart.length > 0 && (
            <div className="px-4 py-3 border-t bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDelivery ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    {isDelivery ? <Truck className="w-5 h-5 text-blue-600" /> : <Store className="w-5 h-5 text-gray-600" />}
                  </div>
                  <div>
                    <p className="font-medium">{isDelivery ? 'Delivery' : 'In-Store Pickup'}</p>
                    <p className="text-sm text-gray-500">{isDelivery ? '+$5.99 delivery fee' : 'Customer takes items now'}</p>
                  </div>
                </div>
                <Switch checked={isDelivery} onCheckedChange={setIsDelivery} />
              </div>
              
              {isDelivery && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Input 
                    placeholder="Customer Name *" 
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    className="no-scanner"
                  />
                  <Input 
                    placeholder="Phone *" 
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="no-scanner"
                  />
                  <Input 
                    placeholder="Delivery Address *" 
                    value={customerInfo.address}
                    onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                    className="col-span-2 no-scanner"
                  />
                </div>
              )}
            </div>
          )}

          {/* Payment Section */}
          {cart.length > 0 && (
            <div className="p-3 sm:p-4 border-t bg-white">
              {/* Totals */}
              <div className="space-y-1 mb-3 sm:mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax (HST 13%):</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount:</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {isDelivery && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery:</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg sm:text-xl font-bold pt-2 border-t">
                  <span>TOTAL:</span>
                  <span className="text-green-600">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 sm:mb-4">
                {PAYMENT_METHODS.map(method => {
                  const Icon = method.icon;
                  return (
                    <Button
                      key={method.id}
                      variant={paymentMethod === method.id ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === method.id ? `bg-${method.color}-500 hover:bg-${method.color}-600` : ''
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <span className="text-xs">{method.name}</span>
                    </Button>
                  );
                })}
              </div>

              {/* Cash Payment */}
              {paymentMethod === 'cash' && (
                <div className="mb-4">
                  <Label className="text-sm font-medium mb-2 block">Cash Received</Label>
                  <div className="flex gap-2 mb-2">
                    {quickAmounts.map(amount => (
                      <Button 
                        key={amount}
                        variant="outline" 
                        size="sm"
                        onClick={() => setCashReceived(amount.toString())}
                        className="flex-1"
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="text-xl font-bold text-center no-scanner"
                  />
                  {change > 0 && (
                    <div className="mt-2 p-3 bg-green-100 rounded-lg text-center">
                      <span className="text-sm text-green-700">Change Due:</span>
                      <span className="text-2xl font-bold text-green-700 ml-2">${change.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Gift Card Payment */}
              {paymentMethod === 'giftcard' && (
                <div className="mb-4">
                  <Label className="text-sm font-medium mb-2 block">Gift Card Code</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter gift card code"
                      value={giftCardCode}
                      onChange={(e) => setGiftCardCode(e.target.value)}
                      className="no-scanner"
                    />
                    <Button onClick={checkGiftCardBalance} variant="outline">
                      Check
                    </Button>
                  </div>
                  {giftCardBalance !== null && (
                    <p className="mt-2 text-sm text-green-600">
                      Balance: ${giftCardBalance.toFixed(2)}
                    </p>
                  )}
                </div>
              )}

              {/* Complete Transaction Button */}
              <Button 
                className={`w-full h-14 text-lg ${activeTransfer ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                onClick={processTransaction}
                disabled={processing || cart.length === 0}
                data-testid="complete-sale-btn"
              >
                {processing ? (
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                ) : activeTransfer ? (
                  <Download className="w-6 h-6 mr-2" />
                ) : (
                  <Check className="w-6 h-6 mr-2" />
                )}
                {activeTransfer ? `Complete Transfer - $${total.toFixed(2)}` : `Complete Sale - $${total.toFixed(2)}`}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceipt && lastOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md p-6 m-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Transaction Complete!</h2>
              <p className="text-gray-500">Order #{lastOrder.order_number}</p>
            </div>
            
            <div className="space-y-2 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between">
                <span>Items:</span>
                <span>{lastOrder.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="font-bold">${lastOrder.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Payment:</span>
                <span className="capitalize">{lastOrder.payment_method}</span>
              </div>
              {lastOrder.change_due > 0 && (
                <div className="flex justify-between text-green-600 font-bold">
                  <span>Change:</span>
                  <span>${lastOrder.change_due.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowReceipt(false)}
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
              <Button 
                className="flex-1 bg-blue-500 hover:bg-blue-600"
                onClick={printReceipt}
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Receipt
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Camera Barcode Scanner - Same as homepage "Scan to Cart" */}
      <CameraBarcodeScanner
        isOpen={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={(barcode, product) => {
          console.log('POS Barcode scanned:', barcode, product?.name);
        }}
        products={allProducts}
        onAddToCart={(product) => {
          addToCart(product);
        }}
        mode="cart"
      />
    </div>
  );
};

export default POSCashier;
